import * as i1$2 from '@angular/cdk/overlay';
import { Overlay, OverlayConfig, OverlayModule } from '@angular/cdk/overlay';
import { BasePortalOutlet, CdkPortalOutlet, ComponentPortal, TemplatePortal, PortalModule } from '@angular/cdk/portal';
import * as i0 from '@angular/core';
import { EventEmitter, ViewChild, Optional, Inject, ChangeDetectionStrategy, Component, InjectionToken, inject, Injector, TemplateRef, SkipSelf, Injectable, Input, Directive, Output, NgModule } from '@angular/core';
import { Subject, defer } from 'rxjs';
import { filter, take, startWith, takeUntil } from 'rxjs/operators';
import { _getFocusedElementPierceShadowDom } from '@angular/cdk/platform';
import { DOCUMENT, NgTemplateOutlet, CommonModule } from '@angular/common';
import * as i4 from '@aposin/ng-aquila/button';
import { NxButtonModule } from '@aposin/ng-aquila/button';
import * as i1$1 from '@aposin/ng-aquila/icon';
import { NxIconModule } from '@aposin/ng-aquila/icon';
import { trigger, state, transition, style, animate, animation, useAnimation, query, animateChild } from '@angular/animations';
import * as i1 from '@angular/cdk/a11y';
import { CdkTrapFocus, A11yModule } from '@angular/cdk/a11y';
import { ESCAPE, hasModifierKey } from '@angular/cdk/keycodes';
import { CdkScrollable } from '@angular/cdk/scrolling';
import * as i3 from '@angular/platform-browser';

/**
 * Configuration for opening a modal  with the NxDialogService.
 */
const _c0 = ["closeButton"];
const _c1 = a0 => ({
  value: a0
});
function NxModalContainer_ng_template_0_Template(rf, ctx) {}
function NxModalContainer_Conditional_1_Template(rf, ctx) {
  if (rf & 1) {
    const _r1 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 3, 0);
    i0.ɵɵlistener("click", function NxModalContainer_Conditional_1_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r1);
      const ctx_r1 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r1._closeButtonClick());
    });
    i0.ɵɵelement(2, "nx-icon", 4);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r1 = i0.ɵɵnextContext();
    i0.ɵɵattribute("aria-label", ctx_r1._config.closeIconButtonLabel);
  }
}
const _c2 = ["nxModalTitle", ""];
const _c3 = ["*"];
function NxModalTitleComponent_Conditional_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelement(0, "nx-status-icon", 0);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("type", ctx_r0.status);
  }
}
function NxModalComponent_Conditional_5_ng_container_0_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵelementContainer(0);
  }
}
function NxModalComponent_Conditional_5_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵtemplate(0, NxModalComponent_Conditional_5_ng_container_0_Template, 1, 0, "ng-container", 7);
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵproperty("ngTemplateOutlet", ctx_r0.body);
  }
}
function NxModalComponent_Conditional_6_Template(rf, ctx) {
  if (rf & 1) {
    i0.ɵɵprojection(0);
  }
}
function NxModalComponent_Conditional_7_Template(rf, ctx) {
  if (rf & 1) {
    const _r2 = i0.ɵɵgetCurrentView();
    i0.ɵɵelementStart(0, "button", 8, 0);
    i0.ɵɵlistener("click", function NxModalComponent_Conditional_7_Template_button_click_0_listener() {
      i0.ɵɵrestoreView(_r2);
      const ctx_r0 = i0.ɵɵnextContext();
      return i0.ɵɵresetView(ctx_r0.closeButtonClick());
    });
    i0.ɵɵelement(2, "nx-icon", 9);
    i0.ɵɵelementEnd();
  }
  if (rf & 2) {
    const ctx_r0 = i0.ɵɵnextContext();
    i0.ɵɵattribute("aria-label", ctx_r0.closeButtonLabel);
  }
}
class NxModalConfig {
  constructor() {
    /** The ARIA role of the modal element. */
    this.role = 'dialog';
    /** Custom class for the overlay pane. */
    this.panelClass = '';
    /** Whether the modal has a backdrop. */
    this.hasBackdrop = true;
    /** Custom class for the backdrop. */
    this.backdropClass = '';
    /** Whether the user can use escape or clicking on the backdrop to close the modal. */
    this.disableClose = false;
    /** Whether the modal is to be shown in fullscreen. When set to true, the width, height, maxWidth and maxHeight are overwritten */
    this.fullscreen = false;
    /** Width of the modal. */
    this.width = '736px';
    /** Height of the modal. */
    this.height = '';
    /** Max-width of the modal. If a number is provided, assumes pixel units. Defaults to '736px'. */
    this.maxWidth = '736px';
    /** Data being injected into the child component. */
    this.data = null;
    /** ID of the element that describes the modal. */
    this.ariaDescribedBy = null;
    /** ID of the element that labels the modal. */
    this.ariaLabelledBy = null;
    /** Aria label to assign to the modal element. */
    this.ariaLabel = null;
    /** Whether the modal should focus the first focusable element on open. */
    this.autoFocus = true;
    /**
     * Whether the modal should restore focus to the
     * previously-focused element, after it's closed.
     */
    this.restoreFocus = true;
    /**
     * Whether the modal should close when the user goes backwards/forwards in history.
     * Note that this usually doesn't include clicking on links (unless the user is using
     * the `HashLocationStrategy`).
     */
    this.closeOnNavigation = true;
    /**
     * Whether a close button with icon should be displayed in the top right corner of the modal.
     *
     * Default: `false`.
     */
    this.showCloseIcon = false;
    /**
     * Sets the 'aria-label' of the modal close button needed for accessibility.
     *
     * Default: `'Close dialog'`.
     */
    this.closeIconButtonLabel = 'Close dialog';
    /**
     * Sets locale direction for the modal.
     *
     * Default: `'ltr'`.
     */
    this.direction = 'ltr';
    /** Define custom function to determine whether a modal can be closed  */
    this.shouldClose = () => true;
  }
}

/**
 * Animations used by the `NxModalContainer`.
 * @docs-private
 */
const NxModalAnimations = {
  /** Animation that is applied on the modal container by defalt. */
  modalContainer: trigger('modalContainer', [
  // Note: The `enter` animation transitions to `transform: none`, because for some reason
  // specifying the transform explicitly, causes IE both to blur the modal content and
  // decimate the animation performance. Leaving it as `none` solves both issues.
  state('void, exit', style({
    opacity: 0,
    transform: 'scale(1.3)'
  })), state('enter', style({
    transform: 'none'
  })), transition('* => enter', animate('200ms cubic-bezier(0, 0, 0.2, 1)', style({
    transform: 'scale(1)',
    opacity: 1
  }))), transition('* => void, * => exit', animate('200ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({
    opacity: 0,
    transform: 'scale(1.3)'
  })))]),
  modalContainerFullscreen: trigger('slideInOut', [state('void, exit', style({
    opacity: 0,
    transform: 'translateY(100%)'
  })), state('enter', style({
    transform: 'none'
  })), transition('* => enter', animate('300ms ease-out', style({
    transform: 'translateY(0%)',
    opacity: 1
  }))), transition('* => void, * => exit', animate('300ms ease-out', style({
    opacity: 0,
    transform: 'translateY(100%)'
  })))])
};

/**
 * Throws an exception for the case when a ComponentPortal is
 * attached to a DomPortalOutlet without an origin.
 * @docs-private
 */
function throwNxDialogContentAlreadyAttachedError() {
  throw Error('Attempting to attach modal content after content is already attached');
}
/**
 * Internal component that wraps user-provided modal content.
 * Animation is based on https://material.io/guidelines/motion/choreography.html.
 * @docs-private
 */
class NxModalContainer extends BasePortalOutlet {
  constructor(_elementRef, _focusTrapFactory, _cdr, _document, /** The modal configuration. */
  _config, _focusMonitor, _interactivityChecker, _ngZone) {
    super();
    this._elementRef = _elementRef;
    this._focusTrapFactory = _focusTrapFactory;
    this._cdr = _cdr;
    this._document = _document;
    this._config = _config;
    this._focusMonitor = _focusMonitor;
    this._interactivityChecker = _interactivityChecker;
    this._ngZone = _ngZone;
    /** Element that was focused before the modal was opened. Save this to restore upon close. */
    this._elementFocusedBeforeDialogWasOpened = null;
    /** State of the modal animation. */
    this._state = 'enter';
    /** Emits when an animation state changes. */
    this._animationStateChanged = new EventEmitter();
    /** Emits when the close button (X) is clicked. */
    this._closeButtonClicked = new EventEmitter();
    /** for appearance of modal */
    this._isExpert = false;
    /**
     * Attaches a DOM portal to the modal container.
     * @param portal Portal to be attached.
     * @deprecated To be turned into a method when changed in the CDK.
     */
    this.attachDomPortal = portal => {
      if (this._portalOutlet.hasAttached()) {
        throwNxDialogContentAlreadyAttachedError();
      }
      this._savePreviouslyFocusedElement();
      return this._portalOutlet.attachDomPortal(portal);
    };
    this._ariaLabelledBy = _config.ariaLabelledBy || null;
  }
  ngAfterViewInit() {
    if (this._config.showCloseIcon) {
      this._focusMonitor.monitor(this._closeButton);
    }
  }
  ngOnInit() {
    const appearance = this._config.appearance;
    this._isExpert = appearance === 'expert';
  }
  ngOnDestroy() {
    this._focusMonitor.stopMonitoring(this._closeButton);
  }
  /**
   * Attach a ComponentPortal as content to this modal container.
   * @param portal Portal to be attached as the modal content.
   */
  attachComponentPortal(portal) {
    if (this._portalOutlet.hasAttached()) {
      throwNxDialogContentAlreadyAttachedError();
    }
    this._savePreviouslyFocusedElement();
    return this._portalOutlet.attachComponentPortal(portal);
  }
  /**
   * Attach a TemplatePortal as content to this modal container.
   * @param portal Portal to be attached as the modal content.
   */
  attachTemplatePortal(portal) {
    if (this._portalOutlet.hasAttached()) {
      throwNxDialogContentAlreadyAttachedError();
    }
    this._savePreviouslyFocusedElement();
    return this._portalOutlet.attachTemplatePortal(portal);
  }
  /** Moves the focus inside the focus trap. */
  _trapFocus() {
    const dialog = this._elementRef.nativeElement;
    if (!this._focusTrap) {
      this._focusTrap = this._focusTrapFactory.create(dialog);
    }
    const activeElement = this._document?.activeElement;
    switch (this._config.autoFocus) {
      // Otherwise ensure that focus is on the modal container. It's possible that a different
      // component tried to move focus while the open animation was running. See:
      // https://github.com/angular/components/issues/16215. Note that we only want to do this
      // if the focus isn't inside the modal already, because it's possible that the consumer
      // turned off `autoFocus` in order to move focus themselves.
      case false:
      case 'dialog':
        if (activeElement !== dialog && !dialog.contains(activeElement)) {
          dialog.focus();
        }
        return;
      // If we were to attempt to focus immediately, then the content of the modal would not yet be
      // ready in instances where change detection has to run first. To deal with this, we simply
      // wait for the microtask queue to be empty.
      case true:
      case 'first-tabbable':
        this._focusTrap.focusInitialElementWhenReady().then(() => {
          const focused = dialog?.querySelector('.cdk-focused');
          if (focused) {
            // make focus style appear because it only show on focus vis keyboard
            this._focusMonitor.focusVia(focused, 'keyboard');
          }
        });
        break;
      case 'first-heading':
        this._focusByCssSelector('h1, h2, h3, h4, h5, h6, [role="heading"]');
        break;
      default:
        this._focusByCssSelector(this._config.autoFocus);
        break;
    }
    // fallback, if no tabbable nor selector then focus on dialog.
    const hasTabbable = dialog.contains(document.activeElement);
    if (!hasTabbable) {
      dialog.focus();
    }
  }
  _focusByCssSelector(selector, options) {
    const elementToFocus = this._elementRef.nativeElement.querySelector(selector);
    if (elementToFocus) {
      this._forceFocus(elementToFocus, options);
    }
  }
  _forceFocus(element, options) {
    if (!this._interactivityChecker.isFocusable(element)) {
      element.tabIndex = -1;
      // The tabindex attribute should be removed to avoid navigating to that element again
      this._ngZone.runOutsideAngular(() => {
        const callback = () => {
          element.removeEventListener('blur', callback);
          element.removeEventListener('mousedown', callback);
          element.removeAttribute('tabindex');
        };
        element.addEventListener('blur', callback);
        element.addEventListener('mousedown', callback);
      });
    }
    element.focus(options);
  }
  /** Restores focus to the element that was focused before the modal opened. */
  _restoreFocus() {
    const toFocus = this._elementFocusedBeforeDialogWasOpened;
    // We need the extra check, because IE can set the `activeElement` to null in some cases.
    if (this._config.restoreFocus && toFocus && typeof toFocus.focus === 'function') {
      const activeElement = _getFocusedElementPierceShadowDom();
      const element = this._elementRef.nativeElement;
      // Make sure that focus is still inside the modal or is on the body (usually because a
      // non-focusable element like the backdrop was clicked) before moving it. It's possible that
      // the consumer moved it themselves before the animation was done, in which case we shouldn't
      // do anything.
      if (!activeElement || activeElement === this._document?.body || activeElement === element || element.contains(activeElement)) {
        this._focusMonitor.focusVia(toFocus, 'keyboard');
      }
    }
    if (this._focusTrap) {
      this._focusTrap.destroy();
    }
  }
  /** Saves a reference to the element that was focused before the modal was opened. */
  _savePreviouslyFocusedElement() {
    if (!this._document) {
      return;
    }
    this._elementFocusedBeforeDialogWasOpened = _getFocusedElementPierceShadowDom();
    // Note that there is no focus method when rendering on the server.
    if (this._elementRef.nativeElement.focus) {
      //  prevent the user from accidentally
      // opening multiple modals at the same time.
      this._elementFocusedBeforeDialogWasOpened?.blur();
    }
  }
  /** Callback, invoked whenever an animation on the host completes. */
  _onAnimationDone(event) {
    if (event.toState === 'enter') {
      this._trapFocus();
    } else if (event.toState === 'exit') {
      this._restoreFocus();
    }
    this._animationStateChanged.emit(event);
  }
  /** Callback, invoked when an animation on the host starts. */
  _onAnimationStart(event) {
    this._animationStateChanged.emit(event);
  }
  /** Starts the modal exit animation. */
  _startExitAnimation() {
    this._state = 'exit';
    // Mark the container for check so it can react if the
    // view container is using OnPush change detection.
    this._cdr.markForCheck();
  }
  _closeButtonClick() {
    this._closeButtonClicked.emit();
  }
  static {
    this.ɵfac = function NxModalContainer_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalContainer)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i1.FocusTrapFactory), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(DOCUMENT, 8), i0.ɵɵdirectiveInject(NxModalConfig), i0.ɵɵdirectiveInject(i1.FocusMonitor), i0.ɵɵdirectiveInject(i1.InteractivityChecker), i0.ɵɵdirectiveInject(i0.NgZone));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxModalContainer,
      selectors: [["nx-modal-container"]],
      viewQuery: function NxModalContainer_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(CdkPortalOutlet, 7);
          i0.ɵɵviewQuery(_c0, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._portalOutlet = _t.first);
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._closeButton = _t.first);
        }
      },
      hostAttrs: ["tabindex", "-1", "aria-modal", "true", 1, "nx-modal__container"],
      hostVars: 13,
      hostBindings: function NxModalContainer_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵsyntheticHostListener("@modalContainer.start", function NxModalContainer_animation_modalContainer_start_HostBindingHandler($event) {
            return ctx._onAnimationStart($event);
          })("@modalContainer.done", function NxModalContainer_animation_modalContainer_done_HostBindingHandler($event) {
            return ctx._onAnimationDone($event);
          })("@slideInOut.start", function NxModalContainer_animation_slideInOut_start_HostBindingHandler($event) {
            return ctx._onAnimationStart($event);
          })("@slideInOut.done", function NxModalContainer_animation_slideInOut_done_HostBindingHandler($event) {
            return ctx._onAnimationDone($event);
          });
        }
        if (rf & 2) {
          i0.ɵɵsyntheticHostProperty("@modalContainer", i0.ɵɵpureFunction1(9, _c1, !ctx._config.fullscreen ? ctx._state : ""))("@slideInOut", i0.ɵɵpureFunction1(11, _c1, ctx._config.fullscreen ? ctx._state : ""));
          i0.ɵɵattribute("id", ctx._id)("role", ctx._config.role)("aria-labelledby", ctx._config.ariaLabel ? null : ctx._ariaLabelledBy)("aria-label", ctx._config.ariaLabel)("aria-describedby", ctx._config.ariaDescribedBy || null);
          i0.ɵɵclassProp("is-expert", ctx._isExpert);
        }
      },
      features: [i0.ɵɵInheritDefinitionFeature],
      decls: 2,
      vars: 1,
      consts: [["closeButton", ""], ["cdkPortalOutlet", ""], ["nxPlainButton", "", "type", "button", 1, "nx-modal__close"], ["nxPlainButton", "", "type", "button", 1, "nx-modal__close", 3, "click"], ["name", "close", "aria-hidden", "true"]],
      template: function NxModalContainer_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵtemplate(0, NxModalContainer_ng_template_0_Template, 0, 0, "ng-template", 1)(1, NxModalContainer_Conditional_1_Template, 3, 1, "button", 2);
        }
        if (rf & 2) {
          i0.ɵɵadvance();
          i0.ɵɵconditional(ctx._config.showCloseIcon ? 1 : -1);
        }
      },
      dependencies: [NxIconModule, i1$1.NxIconComponent, CdkPortalOutlet, NxButtonModule, i4.NxPlainButtonComponent],
      styles: [".cdk-overlay-pane.is-fullscreen{padding-top:32px}@media (max-width: 703px){  .cdk-overlay-pane.is-fullscreen{padding-top:8px}}[_nghost-%COMP%]{background:var(--modal-background-color);color:var(--modal-text-color);margin:0 32px;padding:40px;border-radius:4px;box-shadow:var(--shadow-large);display:block;position:relative;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;min-height:inherit;max-height:inherit}.is-fullscreen   [_nghost-%COMP%]{border-radius:8px 8px 0 0}@media (max-width: 703px){[_nghost-%COMP%]{margin:0 8px;padding:32px}}@media screen and (forced-colors: active){[_nghost-%COMP%]{border:2px solid CanvasText}}[_nghost-%COMP%]     .nx-modal__title{text-align:center;font-size:1.875rem;font-weight:300;line-height:2.25rem;display:flex;align-items:center;flex-direction:column;margin:0 0 24px}@media (max-width: 703px){[_nghost-%COMP%]     .nx-modal__title{font-size:1.625rem}}[_nghost-%COMP%]     .nx-modal__status{margin-bottom:16px;font-size:4rem}.is-expert[_nghost-%COMP%]     .nx-modal__status{display:flex;margin-bottom:0;margin-right:16px;font-size:2.25rem!important}.is-expert[_nghost-%COMP%]     .nx-modal__title{font-weight:400;text-align:left;flex-direction:row;margin-bottom:16px}.is-expert[_nghost-%COMP%]     .nx-modal__actions{justify-content:flex-end}[_nghost-%COMP%]     .nx-modal__content{display:block;margin:0 -40px;padding:0 40px;max-height:65vh;overflow:auto;-webkit-overflow-scrolling:touch}.is-fullscreen   [_nghost-%COMP%]     .nx-modal__content{max-height:calc(100% - 32px)}@media (max-width: 703px){[_nghost-%COMP%]     .nx-modal__content{margin:0 -32px;padding:0 32px}.is-fullscreen   [_nghost-%COMP%]     .nx-modal__content{max-height:calc(100% - 40px)}}[_nghost-%COMP%]     .nx-modal__actions{margin-bottom:-40px;margin-left:-40px;margin-right:-40px;height:72px;border-top:1px solid var(--modal-actions-border-color);display:flex;align-items:center;justify-content:flex-end;padding:0 40px;border-bottom-left-radius:4px;border-bottom-right-radius:4px}.is-fullscreen   [_nghost-%COMP%]     .nx-modal__actions{background:var(--modal-background-color);position:absolute;bottom:40px;width:100%}@media (max-width: 703px){[_nghost-%COMP%]     .nx-modal__actions{margin-bottom:-32px;margin-left:-32px;margin-right:-32px;padding:0 32px}.is-fullscreen   [_nghost-%COMP%]     .nx-modal__actions{bottom:32px}}.nx-modal__close[_ngcontent-%COMP%]{position:absolute;top:16px;right:16px;height:24px}.nx-modal__close[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{font-size:24px}[dir=rtl][_nghost-%COMP%]   .nx-modal__close[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-modal__close[_ngcontent-%COMP%]{right:auto;left:16px}"],
      data: {
        animation: [NxModalAnimations.modalContainer, NxModalAnimations.modalContainerFullscreen]
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalContainer, [{
    type: Component,
    args: [{
      selector: 'nx-modal-container',
      changeDetection: ChangeDetectionStrategy.Default,
      animations: [NxModalAnimations.modalContainer, NxModalAnimations.modalContainerFullscreen],
      host: {
        class: 'nx-modal__container',
        tabindex: '-1',
        'aria-modal': 'true',
        '[attr.id]': '_id',
        '[attr.role]': '_config.role',
        '[attr.aria-labelledby]': '_config.ariaLabel ? null : _ariaLabelledBy',
        '[attr.aria-label]': '_config.ariaLabel',
        '[attr.aria-describedby]': '_config.ariaDescribedBy || null',
        '[class.is-expert]': '_isExpert',
        '[@modalContainer]': '{ value: !_config.fullscreen ? _state : "" }',
        '(@modalContainer.start)': '_onAnimationStart($event)',
        '(@modalContainer.done)': '_onAnimationDone($event)',
        '[@slideInOut]': '{ value: _config.fullscreen ? _state : "" }',
        '(@slideInOut.start)': '_onAnimationStart($event)',
        '(@slideInOut.done)': '_onAnimationDone($event)'
      },
      imports: [NxIconModule, CdkPortalOutlet, NxButtonModule],
      template: "<ng-template cdkPortalOutlet></ng-template>\n@if (_config.showCloseIcon) {\n    <button nxPlainButton #closeButton [attr.aria-label]=\"_config.closeIconButtonLabel\" (click)=\"_closeButtonClick()\" class=\"nx-modal__close\" type=\"button\">\n        <nx-icon name=\"close\" aria-hidden=\"true\"></nx-icon>\n    </button>\n}\n",
      styles: ["::ng-deep .cdk-overlay-pane.is-fullscreen{padding-top:32px}@media (max-width: 703px){::ng-deep .cdk-overlay-pane.is-fullscreen{padding-top:8px}}:host{background:var(--modal-background-color);color:var(--modal-text-color);margin:0 32px;padding:40px;border-radius:4px;box-shadow:var(--shadow-large);display:block;position:relative;box-sizing:border-box;overflow:auto;outline:0;width:100%;height:100%;min-height:inherit;max-height:inherit}.is-fullscreen :host{border-radius:8px 8px 0 0}@media (max-width: 703px){:host{margin:0 8px;padding:32px}}@media screen and (forced-colors: active){:host{border:2px solid CanvasText}}:host ::ng-deep .nx-modal__title{text-align:center;font-size:1.875rem;font-weight:300;line-height:2.25rem;display:flex;align-items:center;flex-direction:column;margin:0 0 24px}@media (max-width: 703px){:host ::ng-deep .nx-modal__title{font-size:1.625rem}}:host ::ng-deep .nx-modal__status{margin-bottom:16px;font-size:4rem}:host(.is-expert) ::ng-deep .nx-modal__status{display:flex;margin-bottom:0;margin-right:16px;font-size:2.25rem!important}:host(.is-expert) ::ng-deep .nx-modal__title{font-weight:400;text-align:left;flex-direction:row;margin-bottom:16px}:host(.is-expert) ::ng-deep .nx-modal__actions{justify-content:flex-end}:host ::ng-deep .nx-modal__content{display:block;margin:0 -40px;padding:0 40px;max-height:65vh;overflow:auto;-webkit-overflow-scrolling:touch}.is-fullscreen :host ::ng-deep .nx-modal__content{max-height:calc(100% - 32px)}@media (max-width: 703px){:host ::ng-deep .nx-modal__content{margin:0 -32px;padding:0 32px}.is-fullscreen :host ::ng-deep .nx-modal__content{max-height:calc(100% - 40px)}}:host ::ng-deep .nx-modal__actions{margin-bottom:-40px;margin-left:-40px;margin-right:-40px;height:72px;border-top:1px solid var(--modal-actions-border-color);display:flex;align-items:center;justify-content:flex-end;padding:0 40px;border-bottom-left-radius:4px;border-bottom-right-radius:4px}.is-fullscreen :host ::ng-deep .nx-modal__actions{background:var(--modal-background-color);position:absolute;bottom:40px;width:100%}@media (max-width: 703px){:host ::ng-deep .nx-modal__actions{margin-bottom:-32px;margin-left:-32px;margin-right:-32px;padding:0 32px}.is-fullscreen :host ::ng-deep .nx-modal__actions{bottom:32px}}.nx-modal__close{position:absolute;top:16px;right:16px;height:24px}.nx-modal__close nx-icon{font-size:24px}:host-context([dir=rtl]) .nx-modal__close{right:auto;left:16px}\n"]
    }]
  }], () => [{
    type: i0.ElementRef
  }, {
    type: i1.FocusTrapFactory
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: Document,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [DOCUMENT]
    }]
  }, {
    type: NxModalConfig
  }, {
    type: i1.FocusMonitor
  }, {
    type: i1.InteractivityChecker
  }, {
    type: i0.NgZone
  }], {
    _portalOutlet: [{
      type: ViewChild,
      args: [CdkPortalOutlet, {
        static: true
      }]
    }],
    _closeButton: [{
      type: ViewChild,
      args: ['closeButton']
    }]
  });
})();

// Id counter
let nexId = 0;
/**
 * Reference to a modal opened via the NxDialogService.
 */
class NxModalRef {
  constructor(_overlayRef, _containerInstance, id = `nx-modal-${nexId++}`) {
    this._overlayRef = _overlayRef;
    this._containerInstance = _containerInstance;
    this.id = id;
    /** Whether the user is allowed to close the modal. */
    this.disableClose = this._containerInstance._config.disableClose;
    /** Define custom function to determine whether a modal can be closed  */
    this.shouldClose = this._containerInstance._config.shouldClose;
    /** Stream that emits when closing modal has been denied */
    this.closeDenied = new Subject();
    /** Subject for notifying the user that the modal has finished opening. */
    this._afterOpened = new Subject();
    /** Subject for notifying the user that the modal has finished closing. */
    this._afterClosed = new Subject();
    /** Subject for notifying the user that the modal has started closing. */
    this._beforeClosed = new Subject();
    /** Current state of the modal. */
    this._state = 0 /* NxModalState.OPEN */;
    // Pass the id along to the container.
    _containerInstance._id = id;
    // Emit when opening animation completes
    _containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'done' && event.toState === 'enter'), take(1)).subscribe(() => {
      this._afterOpened.next();
      this._afterOpened.complete();
    });
    // Dispose overlay when closing animation is complete
    _containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'done' && event.toState === 'exit'), take(1)).subscribe(() => {
      clearTimeout(this._closeFallbackTimeout);
      this._overlayRef.dispose();
    });
    _containerInstance._closeButtonClicked.subscribe(() => {
      this.close();
    });
    _overlayRef.detachments().subscribe(() => {
      this._beforeClosed.next(this._result);
      this._beforeClosed.complete();
      this._afterClosed.next(this._result);
      this._afterClosed.complete();
      this.componentInstance = null;
      this._overlayRef.dispose();
    });
    _overlayRef.keydownEvents().pipe(filter(event => event.keyCode === ESCAPE && !this.disableClose && !hasModifierKey(event))).subscribe(event => {
      event.preventDefault();
      this.close();
    });
  }
  /**
   * Close the modal.
   * @param modalResult Optional result to return to the modal opener.
   */
  close(modalResult) {
    this._result = modalResult;
    const shouldClose = this.shouldClose?.(modalResult) ?? true;
    if (!shouldClose) {
      this.closeDenied.next();
      return;
    }
    // Transition the backdrop in parallel to the modal.
    this._containerInstance._animationStateChanged.pipe(filter(event => event.phaseName === 'start'), take(1)).subscribe(event => {
      this._beforeClosed.next(modalResult);
      this._beforeClosed.complete();
      this._state = 2 /* NxModalState.CLOSED */;
      this._overlayRef.detachBackdrop();
      // The logic that disposes of the overlay depends on the exit animation completing, however
      // it isn't guaranteed if the parent view is destroyed while it's running. Add a fallback
      // timeout which will clean everything up if the animation hasn't fired within the specified
      // amount of time plus 100ms. We don't need to run this outside the NgZone, because for the
      // vast majority of cases the timeout will have been cleared before it has the chance to fire.
      this._closeFallbackTimeout = setTimeout(() => {
        this._overlayRef.dispose();
      }, event.totalTime + 100);
    });
    this._containerInstance._startExitAnimation();
    this._state = 1 /* NxModalState.CLOSING */;
  }
  /**
   * Gets an observable that is notified when the modal is finished opening.
   */
  afterOpened() {
    return this._afterOpened.asObservable();
  }
  /**
   * Gets an observable that is notified when the modal is finished closing.
   */
  afterClosed() {
    return this._afterClosed.asObservable();
  }
  /**
   * Gets an observable that is notified when the modal has started closing.
   */
  beforeClosed() {
    return this._beforeClosed.asObservable();
  }
  /**
   * Gets an observable that emits when the overlay's backdrop has been clicked.
   */
  backdropClick() {
    return this._overlayRef.backdropClick();
  }
  /**
   * Gets an observable that emits when keydown events are targeted on the overlay.
   */
  keydownEvents() {
    return this._overlayRef.keydownEvents();
  }
  /**
   * Updates the modal's position.
   * @param position New modal position.
   */
  updatePosition(position) {
    const strategy = this._getPositionStrategy();
    if (position && (position.left || position.right)) {
      position.left ? strategy.left(position.left) : strategy.right(position.right);
    } else {
      strategy.centerHorizontally();
    }
    if (position && (position.top || position.bottom)) {
      position.top ? strategy.top(position.top) : strategy.bottom(position.bottom);
    } else {
      strategy.centerVertically();
    }
    this._overlayRef.updatePosition();
    return this;
  }
  /**
   * Updates the modal's width and height.
   * @param width New width of the modal.
   * @param height New height of the modal.
   */
  updateSize(width = '', height = '') {
    this._getPositionStrategy().width(width).height(height);
    this._overlayRef.updatePosition();
    return this;
  }
  /** Add a CSS class or an array of classes to the overlay pane. */
  addPanelClass(classes) {
    this._overlayRef.addPanelClass(classes);
    return this;
  }
  /** Remove a CSS class or an array of classes from the overlay pane. */
  removePanelClass(classes) {
    this._overlayRef.removePanelClass(classes);
    return this;
  }
  /** Gets the current state of the modal's lifecycle. */
  getState() {
    return this._state;
  }
  /** Fetches the position strategy object from the overlay ref. */
  _getPositionStrategy() {
    return this._overlayRef.getConfig().positionStrategy;
  }
}

/** Injection token that can be used to access the data that was passed in to a modal. */
const NX_MODAL_DATA = new InjectionToken('NxModalData');
/** Injection token that can be used to specify default modal options. */
const NX_MODAL_DEFAULT_OPTIONS = new InjectionToken('nx-modal-default-options');
/** Injection token that determines the scroll handling while a modal is open. */
const NX_MODAL_SCROLL_STRATEGY = new InjectionToken('nx-modal-scroll-strategy', {
  providedIn: 'root',
  factory: () => {
    const overlay = inject(Overlay);
    return () => overlay.scrollStrategies.block();
  }
});
/**
 * @docs-private
 */
function NX_MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY(overlay) {
  return () => overlay.scrollStrategies.block();
}
/**
 * @docs-private
 */
const NX_MODAL_SCROLL_STRATEGY_PROVIDER = {
  provide: NX_MODAL_SCROLL_STRATEGY,
  useFactory: NX_MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY,
  deps: [Overlay]
};
/**
 * Service to open Material Design modal modals.
 */
class NxDialogService {
  /** Keeps track of the currently-open modals. */
  get openModals() {
    return this._parentDialogService ? this._parentDialogService.openModals : this._openModalsAtThisLevel;
  }
  /** Keeps track of the currently aria-hidden elements. */
  get ariaHiddenElements() {
    return this._parentDialogService ? this._parentDialogService.ariaHiddenElements : this._ariaHiddenElements;
  }
  /** Stream that emits when a modal has been opened. */
  get afterOpened() {
    return this._parentDialogService ? this._parentDialogService.afterOpened : this._afterOpenedAtThisLevel;
  }
  get _afterAllClosed() {
    const parent = this._parentDialogService;
    return parent ? parent._afterAllClosed : this._afterAllClosedAtThisLevel;
  }
  constructor(_overlay, _injector, _defaultOptions, _parentDialogService, _overlayContainer, _defaultScrollStrategyFactory) {
    this._overlay = _overlay;
    this._injector = _injector;
    this._defaultOptions = _defaultOptions;
    this._parentDialogService = _parentDialogService;
    this._overlayContainer = _overlayContainer;
    this._defaultScrollStrategyFactory = _defaultScrollStrategyFactory;
    this._openModalsAtThisLevel = [];
    this._afterAllClosedAtThisLevel = new Subject();
    this._afterOpenedAtThisLevel = new Subject();
    this._ariaHiddenElements = new Map();
    /**
     * Stream that emits when all open modal have finished closing.
     * Will emit on subscribe if there are no open modals to begin with.
     */
    this.afterAllClosed = defer(() => this.openModals.length ? this._afterAllClosed : this._afterAllClosed.pipe(startWith(undefined)));
    this._destroyed = new Subject();
    /** Strategy factory that will be used to handle scrolling while the modal panel is open. */
    this._scrollStrategyFactory = this._defaultScrollStrategyFactory;
  }
  /**
   * Opens a modal modal containing the given component.
   * @param componentOrTemplateRef Type of the component to load into the dialog, or a TemplateRef to instantiate as the modal content.
   * @param config Extra configuration options.
   * @returns Reference to the newly-opened modal.
   */
  open(componentOrTemplateRef, config) {
    config = _applyConfigDefaults(config, this._defaultOptions || new NxModalConfig());
    if (config.id && this.getModalById(config.id)) {
      throw Error(`Modal with id "${config.id}" exists already. The modal id must be unique.`);
    }
    const overlayRef = this._createOverlay(config);
    const modalContainer = this._attachModalContainer(overlayRef, config);
    const modalRef = this._attachModalContent(componentOrTemplateRef, modalContainer, overlayRef, config);
    // If this is the first modal that we're opening, hide all the non-overlay content.
    if (!this.openModals.length) {
      this._hideNonModalContentFromAssistiveTechnology();
    }
    this.openModals.push(modalRef);
    modalRef.beforeClosed().subscribe(() => this._removeOpenModal(modalRef));
    this.afterOpened.next(modalRef);
    return modalRef;
  }
  /**
   * Closes all of the currently-open modals.
   */
  closeAll() {
    this._closeModals(this.openModals);
  }
  /**
   * Finds an open modal by its id.
   * @param id ID to use when looking up the modal.
   */
  getModalById(id) {
    return this.openModals.find(modal => modal.id === id);
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    // Only close the modals at this level on destroy
    // since the parent service may still be active.
    this._closeModals(this._openModalsAtThisLevel);
    this._afterAllClosedAtThisLevel.complete();
    this._afterOpenedAtThisLevel.complete();
  }
  /**
   * Creates the overlay into which the modal will be loaded.
   * @param config The modal configuration.
   * @returns A promise resolving to the OverlayRef for the created overlay.
   */
  _createOverlay(config) {
    const overlayConfig = this._getOverlayConfig(config);
    return this._overlay.create(overlayConfig);
  }
  /**
   * Creates an overlay config from a modal config.
   * @param modalConfig The modal configuration.
   * @returns The overlay configuration.
   */
  _getOverlayConfig(modalConfig) {
    const state = new OverlayConfig({
      positionStrategy: this._overlay.position().global(),
      scrollStrategy: modalConfig.scrollStrategy || this._scrollStrategyFactory(),
      panelClass: modalConfig.panelClass,
      hasBackdrop: modalConfig.hasBackdrop,
      minWidth: modalConfig.minWidth,
      minHeight: modalConfig.minHeight,
      maxWidth: modalConfig.maxWidth,
      maxHeight: modalConfig.maxHeight,
      disposeOnNavigation: modalConfig.closeOnNavigation,
      direction: modalConfig.direction
    });
    if (modalConfig.backdropClass) {
      state.backdropClass = modalConfig.backdropClass;
    }
    if (modalConfig.fullscreen) {
      state.maxWidth = '';
      state.maxHeight = '';
    }
    return state;
  }
  /**
   * Attaches an NxModalContainer to a dialog's already-created overlay.
   * @param overlay Reference to the dialog's underlying overlay.
   * @param config The modal configuration.
   * @returns A promise resolving to a ComponentRef for the attached container.
   */
  _attachModalContainer(overlay, config) {
    const userInjector = config?.viewContainerRef?.injector;
    const injector = Injector.create({
      parent: userInjector || this._injector,
      providers: [{
        provide: NxModalConfig,
        useValue: config
      }]
    });
    const containerPortal = new ComponentPortal(NxModalContainer, config.viewContainerRef, injector, config.componentFactoryResolver);
    const containerRef = overlay.attach(containerPortal);
    return containerRef.instance;
  }
  /**
   * Attaches the user-provided component to the already-created NxModalContainer.
   * @param componentOrTemplateRef The type of component being loaded into the dialog, or a TemplateRef to instantiate as the content.
   * @param modalContainer Reference to the wrapping NxModalContainer.
   * @param overlayRef Reference to the overlay in which the modal resides.
   * @param config The modal configuration.
   * @returns A promise resolving to the NxModalRef that should be returned to the user.
   */
  _attachModalContent(componentOrTemplateRef, modalContainer, overlayRef, config) {
    // Create a reference to the modal we're creating in order to give the user a handle
    // to modify and close it.
    const modalRef = new NxModalRef(overlayRef, modalContainer, config.id);
    // If fullscreen is set to true, set the width and height to be the the fullscreen with and height
    // Add a class for styling the fullscreen modal
    if (config.fullscreen) {
      config.width = '100%';
      config.height = '100%';
      overlayRef.addPanelClass('is-fullscreen');
    }
    // When the modal backdrop is clicked, we want to close it.
    if (config.hasBackdrop) {
      overlayRef.backdropClick().pipe(takeUntil(this._destroyed)).subscribe(() => {
        if (!modalRef.disableClose) {
          modalRef.close();
        }
      });
    }
    if (componentOrTemplateRef instanceof TemplateRef) {
      modalContainer.attachTemplatePortal(new TemplatePortal(componentOrTemplateRef, null, {
        $implicit: config.data,
        modalRef
      }));
    } else {
      const injector = this._createInjector(config, modalRef, modalContainer);
      const contentRef = modalContainer.attachComponentPortal(new ComponentPortal(componentOrTemplateRef, config.viewContainerRef, injector));
      modalRef.componentInstance = contentRef.instance;
    }
    modalRef.updateSize(config.width, config.height).updatePosition(config.position);
    return modalRef;
  }
  /**
   * Creates a custom injector to be used inside the modal. This allows a component loaded inside
   * of a modal to close itself and, optionally, to return a value.
   * @param config Config object that is used to construct the modal.
   * @param modalRef Reference to the modal.
   * @param modalContainer Modal container element that wraps all of the contents.
   * @returns The custom injector that can be used inside the modal.
   */
  _createInjector(config, modalRef, modalContainer) {
    const userInjector = config?.viewContainerRef?.injector;
    // The NxModalContainer is injected in the portal as the NxModalContainer and the dialog's
    // content are created out of the same ViewContainerRef and as such, are siblings for injector
    // purposes. To allow the hierarchy that is expected, the NxModalContainer is explicitly
    // added to the injection tokens.
    const providers = [{
      provide: NxModalContainer,
      useValue: modalContainer
    }, {
      provide: NX_MODAL_DATA,
      useValue: config.data
    }, {
      provide: NxModalRef,
      useValue: modalRef
    }];
    return Injector.create({
      parent: userInjector || this._injector,
      providers
    });
  }
  /**
   * Removes a modal from the array of open modals.
   * @param modalRef Modal to be removed.
   */
  _removeOpenModal(modalRef) {
    const modalIndex = this.openModals.indexOf(modalRef);
    if (modalIndex === -1) {
      return;
    }
    this.openModals.splice(modalIndex, 1);
    // If all the modals were closed, remove/restore the `aria-hidden` and `inert`
    // to a the siblings and emit to the `afterAllClosed` stream.
    if (this.openModals.length) {
      return;
    }
    // Restore `aria-hidden` and `inert` attributes to siblings and emit `afterAllClosed`.
    this.ariaHiddenElements.forEach((previousValue, element) => {
      if (previousValue) {
        element.setAttribute('aria-hidden', previousValue);
        element.setAttribute('inert', previousValue);
      } else {
        element.removeAttribute('aria-hidden');
        element.removeAttribute('inert');
      }
    });
    this.ariaHiddenElements.clear();
    this._afterAllClosed.next();
  }
  /**
   * Hides all of the content that isn't an overlay from assistive technology.
   */
  _hideNonModalContentFromAssistiveTechnology() {
    const overlayContainer = this._overlayContainer.getContainerElement();
    // Ensure that the overlay container is attached to the DOM.
    if (!overlayContainer.parentElement) {
      return;
    }
    Array.from(overlayContainer.parentElement.children).forEach(sibling => {
      if (sibling !== overlayContainer && sibling.nodeName !== 'SCRIPT' && sibling.nodeName !== 'STYLE' && !sibling.hasAttribute('aria-live')) {
        const ariaHidden = sibling.getAttribute('aria-hidden');
        const inert = sibling.getAttribute('inert');
        this.ariaHiddenElements.set(sibling, ariaHidden);
        this.ariaHiddenElements.set(sibling, inert);
        sibling.setAttribute('aria-hidden', 'true');
        sibling.setAttribute('inert', 'true');
      }
    });
  }
  /** Closes all of the modals in an array. */
  _closeModals(modals) {
    let i = modals.length;
    while (i--) {
      // The `_openModals` property isn't updated after close until the rxjs subscription
      // runs on the next microtask, in addition to modifying the array as we're going
      // through it. We loop through all of them and call close without assuming that
      // they'll be removed from the list instantaneously.
      modals[i].close();
    }
  }
  static {
    this.ɵfac = function NxDialogService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxDialogService)(i0.ɵɵinject(i1$2.Overlay), i0.ɵɵinject(i0.Injector), i0.ɵɵinject(NX_MODAL_DEFAULT_OPTIONS, 8), i0.ɵɵinject(NxDialogService, 12), i0.ɵɵinject(i1$2.OverlayContainer), i0.ɵɵinject(NX_MODAL_SCROLL_STRATEGY));
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NxDialogService,
      factory: NxDialogService.ɵfac,
      providedIn: 'root'
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxDialogService, [{
    type: Injectable,
    args: [{
      providedIn: 'root'
    }]
  }], () => [{
    type: i1$2.Overlay
  }, {
    type: i0.Injector
  }, {
    type: NxModalConfig,
    decorators: [{
      type: Optional
    }, {
      type: Inject,
      args: [NX_MODAL_DEFAULT_OPTIONS]
    }]
  }, {
    type: NxDialogService,
    decorators: [{
      type: Optional
    }, {
      type: SkipSelf
    }]
  }, {
    type: i1$2.OverlayContainer
  }, {
    type: undefined,
    decorators: [{
      type: Inject,
      args: [NX_MODAL_SCROLL_STRATEGY]
    }]
  }], null);
})();
/**
 * Applies default options to the modal config.
 * @param config Config to be modified.
 * @param defaultOptions Default options provided.
 * @returns The new configuration object.
 */
function _applyConfigDefaults(config, defaultOptions) {
  return {
    ...defaultOptions,
    ...config
  };
}

/**
 * Button that will close the current modal.
 */
class NxModalCloseDirective {
  constructor(modalRef, _elementRef, _dialogService) {
    this.modalRef = modalRef;
    this._elementRef = _elementRef;
    this._dialogService = _dialogService;
    /** Defaults to `'button'` to prevents accidental form submits. */
    this.type = 'button';
  }
  ngOnInit() {
    if (!this.modalRef) {
      // When this directive is included in a modal via TemplateRef (rather than being
      // in a Component), the DialogRef isn't available via injection because embedded
      // views cannot be given a custom injector. Instead, we look up the DialogRef by
      // ID. This must occur in `onInit`, as the ID binding for the modal container won't
      // be resolved at constructor time.
      this.modalRef = getClosestDialog(this._elementRef, this._dialogService.openModals);
    }
  }
  ngOnChanges(changes) {
    const proxiedChange = changes.modalResult;
    if (proxiedChange) {
      this.modalResult = proxiedChange.currentValue;
    }
  }
  static {
    this.ɵfac = function NxModalCloseDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalCloseDirective)(i0.ɵɵdirectiveInject(NxModalRef, 8), i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(NxDialogService));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxModalCloseDirective,
      selectors: [["", "nxModalClose", ""]],
      hostVars: 2,
      hostBindings: function NxModalCloseDirective_HostBindings(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵlistener("click", function NxModalCloseDirective_click_HostBindingHandler() {
            return ctx.modalRef.close(ctx.modalResult);
          });
        }
        if (rf & 2) {
          i0.ɵɵattribute("aria-label", ctx.ariaLabel || null)("type", ctx.type);
        }
      },
      inputs: {
        ariaLabel: [0, "aria-label", "ariaLabel"],
        type: "type",
        modalResult: [0, "nxModalClose", "modalResult"]
      },
      features: [i0.ɵɵNgOnChangesFeature]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalCloseDirective, [{
    type: Directive,
    args: [{
      selector: '[nxModalClose]',
      host: {
        '(click)': 'modalRef.close(modalResult)',
        '[attr.aria-label]': 'ariaLabel || null',
        '[attr.type]': 'type'
      },
      standalone: true
    }]
  }], () => [{
    type: NxModalRef,
    decorators: [{
      type: Optional
    }]
  }, {
    type: i0.ElementRef
  }, {
    type: NxDialogService
  }], {
    ariaLabel: [{
      type: Input,
      args: ['aria-label']
    }],
    type: [{
      type: Input
    }],
    modalResult: [{
      type: Input,
      args: ['nxModalClose']
    }]
  });
})();
/**
 * Finds the closest NxModalRef to an element by looking at the DOM.
 * @param element Element relative to which to look for a modal.
 * @param openDialogs References to the currently-open modals.
 */
function getClosestDialog(element, openDialogs) {
  let parent = element.nativeElement.parentElement;
  while (parent && !parent.classList.contains('nx-modal__container')) {
    parent = parent.parentElement;
  }
  return parent ? openDialogs.find(modal => modal.id === parent.id) : null;
}
const fadeIn = animation([style({
  opacity: 0
}), animate('300ms', style({
  opacity: 1
}))]);
const fadeOut = animation(animate('300ms', style({
  opacity: 0
})));
const scaleUp = animation([style({
  transform: 'scale(1)',
  opacity: 1
}), animate('250ms', style({
  transform: 'scale(1.3)',
  opacity: 0
}))]);
const scaleDown = animation([style({
  transform: 'scale(1.3)',
  opacity: 0
}), animate('250ms', style({
  transform: 'scale(1)',
  opacity: 1
}))]);

/** @docs-private */
class NxModalService {
  constructor() {
    this.subject = new Subject();
    this.close$ = this.subject.asObservable();
  }
  close() {
    this.subject.next();
  }
  static {
    this.ɵfac = function NxModalService_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalService)();
    };
  }
  static {
    this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
      token: NxModalService,
      factory: NxModalService.ɵfac
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalService, [{
    type: Injectable
  }], null, null);
})();

/** Container for the action buttons in a modal. Has a fixed position at the bottom of the modal on scroll. */
class NxModalActionsDirective {
  static {
    this.ɵfac = function NxModalActionsDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalActionsDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxModalActionsDirective,
      selectors: [["", "nxModalActions", ""]],
      hostVars: 2,
      hostBindings: function NxModalActionsDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("nx-modal__actions", true);
        }
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalActionsDirective, [{
    type: Directive,
    args: [{
      selector: '[nxModalActions]',
      host: {
        '[class.nx-modal__actions]': 'true'
      },
      standalone: true
    }]
  }], null, null);
})();
/** Scrollable container for the content of a modal. */
class NxModalContentDirective {
  static {
    this.ɵfac = function NxModalContentDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalContentDirective)();
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxModalContentDirective,
      selectors: [["", "nxModalContent", ""]],
      hostVars: 2,
      hostBindings: function NxModalContentDirective_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("nx-modal__content", true);
        }
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalContentDirective, [{
    type: Directive,
    args: [{
      selector: '[nxModalContent]',
      host: {
        '[class.nx-modal__content]': 'true'
      },
      standalone: true
    }]
  }], null, null);
})();
/** Title of a modal. */
class NxModalTitleComponent {
  static {
    this.ɵfac = function NxModalTitleComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalTitleComponent)();
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxModalTitleComponent,
      selectors: [["", "nxModalTitle", ""]],
      hostVars: 2,
      hostBindings: function NxModalTitleComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵclassProp("nx-modal__title", true);
        }
      },
      inputs: {
        status: "status"
      },
      attrs: _c2,
      ngContentSelectors: _c3,
      decls: 2,
      vars: 1,
      consts: [[1, "nx-modal__status", 3, "type"]],
      template: function NxModalTitleComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵtemplate(0, NxModalTitleComponent_Conditional_0_Template, 1, 1, "nx-status-icon", 0);
          i0.ɵɵprojection(1);
        }
        if (rf & 2) {
          i0.ɵɵconditional(ctx.status ? 0 : -1);
        }
      },
      dependencies: [NxIconModule, i1$1.NxStatusIconComponent],
      encapsulation: 2
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalTitleComponent, [{
    type: Component,
    args: [{
      selector: '[nxModalTitle]',
      host: {
        '[class.nx-modal__title]': 'true'
      },
      template: `@if (status) {
            <nx-status-icon [type]="status" class="nx-modal__status"></nx-status-icon>
        }
        <ng-content></ng-content>`,
      imports: [NxIconModule]
    }]
  }], null, {
    status: [{
      type: Input
    }]
  });
})();
class NxModalComponent {
  /**
   * Sets the 'aria-label' of the modal close button needed for accessibility.
   *
   * Default: `'Close dialog'`.
   */
  set closeButtonLabel(value) {
    if (value !== this._closeButtonLabel) {
      this._closeButtonLabel = value;
      this._cdr.markForCheck();
    }
  }
  get closeButtonLabel() {
    return this._closeButtonLabel;
  }
  constructor(modalService, eventManager, _cdr, _focusMonitor) {
    this.modalService = modalService;
    this.eventManager = eventManager;
    this._cdr = _cdr;
    this._focusMonitor = _focusMonitor;
    this._closeButtonLabel = 'Close dialog';
    /**
     * Whether the modal view should close when the user hits the escape key.
     * Default: `true`.
     */
    this.hideOnEsc = true;
    /**
     * Whether the modal view should close when the user clicks on the backdrop.
     * Default: `true`.
     */
    this.hideOnClickOutside = true;
    /**
     * Whether the modal view should have a close icon in the upper right corner.
     * Default: `true`.
     */
    this.showCloseIcon = true;
    /**
     * Controls the width of the dialog.
     * On `auto` the width is controlled by the content width,
     * on `fixed` the dialog gets a fixed width of 736px if the viewport is big enough.
     */
    this.size = 'auto';
    /**
     * An event emitted when the user clicks on the backdrop or uses the built-in close button.
     * This event can be applied in conjunction with the custom open state handling
     * of a modal to close it when requested by the user.
     */
    this.closeEvent = new EventEmitter();
    this._destroyed = new Subject();
  }
  ngOnInit() {
    this.modalService.close$.pipe(takeUntil(this._destroyed)).subscribe(() => this.closeEvent.emit());
    this.removeEventListener = this.eventManager.addEventListener(document.body, 'keyup.esc', () => {
      if (this.hideOnEsc) {
        this.modalService.close();
      }
    });
  }
  ngAfterViewInit() {
    if (this.showCloseIcon) {
      this._focusMonitor.monitor(this._closeButton);
    }
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    this.removeEventListener();
    this._focusMonitor.stopMonitoring(this._closeButton);
  }
  /** @docs-private */
  clickOutsideModal() {
    if (this.hideOnClickOutside) {
      this.modalService.close();
    }
  }
  /** @docs-private */
  closeButtonClick() {
    this.modalService.close();
  }
  /** @docs-private */
  cancelClick(evt) {
    evt.stopPropagation();
  }
  static {
    this.ɵfac = function NxModalComponent_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalComponent)(i0.ɵɵdirectiveInject(NxModalService), i0.ɵɵdirectiveInject(i3.EventManager), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef), i0.ɵɵdirectiveInject(i1.FocusMonitor));
    };
  }
  static {
    this.ɵcmp = /* @__PURE__ */i0.ɵɵdefineComponent({
      type: NxModalComponent,
      selectors: [["nx-modal"]],
      viewQuery: function NxModalComponent_Query(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵviewQuery(_c0, 5);
        }
        if (rf & 2) {
          let _t;
          i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx._closeButton = _t.first);
        }
      },
      hostVars: 3,
      hostBindings: function NxModalComponent_HostBindings(rf, ctx) {
        if (rf & 2) {
          i0.ɵɵsyntheticHostProperty("@fadeInOut", undefined);
          i0.ɵɵclassProp("nx-modal--fixed-width", ctx.size === "fixed");
        }
      },
      inputs: {
        closeButtonLabel: "closeButtonLabel",
        body: [0, "modalBody", "body"],
        hideOnEsc: "hideOnEsc",
        hideOnClickOutside: "hideOnClickOutside",
        showCloseIcon: "showCloseIcon",
        size: [0, "windowSize", "size"]
      },
      outputs: {
        closeEvent: "onModalClose"
      },
      ngContentSelectors: _c3,
      decls: 8,
      vars: 3,
      consts: [["closeButton", ""], ["role", "dialog", "cdkTrapFocus", "", "cdkTrapFocusAutoCapture", "", "cdkFocusInitial", "", 1, "nx-modal"], [1, "nx-modal__backdrop", 3, "click"], [1, "nx-modal__position"], [1, "nx-modal__container", 3, "click"], ["cdkScrollable", "", 1, "nx-modal__content-wrapper"], ["nxPlainButton", "", "type", "button", 1, "nx-modal__close"], [4, "ngTemplateOutlet"], ["nxPlainButton", "", "type", "button", 1, "nx-modal__close", 3, "click"], ["name", "close", "aria-hidden", "true"]],
      template: function NxModalComponent_Template(rf, ctx) {
        if (rf & 1) {
          i0.ɵɵprojectionDef();
          i0.ɵɵelementStart(0, "div", 1)(1, "div", 2);
          i0.ɵɵlistener("click", function NxModalComponent_Template_div_click_1_listener() {
            return ctx.clickOutsideModal();
          });
          i0.ɵɵelementStart(2, "div", 3)(3, "div", 4);
          i0.ɵɵlistener("click", function NxModalComponent_Template_div_click_3_listener($event) {
            return ctx.cancelClick($event);
          });
          i0.ɵɵelementStart(4, "div", 5);
          i0.ɵɵtemplate(5, NxModalComponent_Conditional_5_Template, 1, 1, "ng-container")(6, NxModalComponent_Conditional_6_Template, 1, 0);
          i0.ɵɵelementEnd();
          i0.ɵɵtemplate(7, NxModalComponent_Conditional_7_Template, 3, 1, "button", 6);
          i0.ɵɵelementEnd()()()();
        }
        if (rf & 2) {
          i0.ɵɵadvance(3);
          i0.ɵɵproperty("@scaleUpDown", undefined);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.body ? 5 : 6);
          i0.ɵɵadvance(2);
          i0.ɵɵconditional(ctx.showCloseIcon ? 7 : -1);
        }
      },
      dependencies: [CdkTrapFocus, NxIconModule, i1$1.NxIconComponent, CdkScrollable, NgTemplateOutlet, NxButtonModule, i4.NxPlainButtonComponent],
      styles: ["[_nghost-%COMP%]{position:relative;z-index:1000}.nx-modal[_ngcontent-%COMP%]{display:flex;position:fixed;width:100vw;height:100vh;inset:0;z-index:1000;align-items:center;justify-content:center;transition:opacity .3s ease}.nx-modal__backdrop[_ngcontent-%COMP%]{background-color:#00000059;height:100%;left:0;position:absolute;top:0;width:100%;z-index:-1}.nx-modal__position[_ngcontent-%COMP%]{position:absolute;display:inline-block;top:50%;left:50%;transform:translate(-50%,-50%);z-index:20}.nx-modal__container[_ngcontent-%COMP%]{border-radius:4px;box-shadow:var(--shadow-large);background:var(--modal-background-color);color:var(--modal-text-color)}.nx-modal__content-wrapper[_ngcontent-%COMP%]{padding:40px 40px 48px;max-height:calc(100vh - 48px);overflow-y:auto}@media (max-width: 703px){.nx-modal__content-wrapper[_ngcontent-%COMP%]{max-height:70vh;padding:40px 32px 48px}}.nx-modal__close[_ngcontent-%COMP%]{position:absolute;top:16px;right:16px;height:24px}.nx-modal__close[_ngcontent-%COMP%]   nx-icon[_ngcontent-%COMP%]{font-size:24px}[dir=rtl][_nghost-%COMP%]   .nx-modal__close[_ngcontent-%COMP%], [dir=rtl]   [_nghost-%COMP%]   .nx-modal__close[_ngcontent-%COMP%]{right:auto;left:16px}@media (max-width: 703px){.nx-modal__position[_ngcontent-%COMP%]{width:100%}.nx-modal__container[_ngcontent-%COMP%]{position:relative;max-width:calc(100vw - 16px);max-height:70vh;margin:0 auto}}.nx-modal--fixed-width[_nghost-%COMP%]   .nx-modal__container[_ngcontent-%COMP%]{width:736px;max-width:calc(100vw - 16px)}[_nghost-%COMP%]     .nx-modal__actions{height:72px;border-top:1px solid var(--modal-actions-border-color);display:flex;align-items:center;justify-content:flex-end;padding:0 40px;position:absolute;bottom:0;left:0;width:100%;border-bottom-left-radius:4px;border-bottom-right-radius:4px;background:var(--modal-actions-background-color)}[_nghost-%COMP%]     .nx-modal__content{overflow-y:auto;max-height:calc(100vh - 120px);padding:0 40px;margin:0 -40px}@media (max-width: 703px){[_nghost-%COMP%]     .nx-modal__content{max-height:calc(70vh - 120px);margin:0 -32px}}@media screen and (forced-colors: active){.nx-modal__content-wrapper[_ngcontent-%COMP%], [_nghost-%COMP%]     .nx-modal__actions{border:2px solid CanvasText}}"],
      data: {
        animation: [trigger('fadeInOut', [transition(':enter', [useAnimation(fadeIn), query('@scaleUpDown', [animateChild()])]), transition(':leave', [query('@scaleUpDown', [animateChild()]), useAnimation(fadeOut)])]), trigger('scaleUpDown', [transition(':enter', useAnimation(scaleDown)), transition(':leave', useAnimation(scaleUp))])]
      },
      changeDetection: 0
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalComponent, [{
    type: Component,
    args: [{
      selector: 'nx-modal',
      changeDetection: ChangeDetectionStrategy.OnPush,
      animations: [trigger('fadeInOut', [transition(':enter', [useAnimation(fadeIn), query('@scaleUpDown', [animateChild()])]), transition(':leave', [query('@scaleUpDown', [animateChild()]), useAnimation(fadeOut)])]), trigger('scaleUpDown', [transition(':enter', useAnimation(scaleDown)), transition(':leave', useAnimation(scaleUp))])],
      host: {
        '[@fadeInOut]': '',
        '[class.nx-modal--fixed-width]': 'size === "fixed"'
      },
      imports: [CdkTrapFocus, NxIconModule, CdkScrollable, NgTemplateOutlet, NxButtonModule],
      template: "<div class=\"nx-modal\" role=\"dialog\" cdkTrapFocus cdkTrapFocusAutoCapture cdkFocusInitial>\n    <div class=\"nx-modal__backdrop\" (click)=\"clickOutsideModal()\">\n        <div class=\"nx-modal__position\">\n            <div class=\"nx-modal__container\" (click)=\"cancelClick($event)\" [@scaleUpDown]>\n                <div class=\"nx-modal__content-wrapper\" cdkScrollable>\n                    @if (body) {\n                        <ng-container *ngTemplateOutlet=\"body\"></ng-container>\n                    } @else {\n                        <ng-content></ng-content>\n                    }\n                </div>\n                @if (showCloseIcon) {\n                    <button nxPlainButton #closeButton (click)=\"closeButtonClick()\" [attr.aria-label]=\"closeButtonLabel\" class=\"nx-modal__close\" type=\"button\">\n                        <nx-icon name=\"close\" aria-hidden=\"true\"></nx-icon>\n                    </button>\n                }\n            </div>\n        </div>\n    </div>\n</div>\n",
      styles: [":host{position:relative;z-index:1000}.nx-modal{display:flex;position:fixed;width:100vw;height:100vh;inset:0;z-index:1000;align-items:center;justify-content:center;transition:opacity .3s ease}.nx-modal__backdrop{background-color:#00000059;height:100%;left:0;position:absolute;top:0;width:100%;z-index:-1}.nx-modal__position{position:absolute;display:inline-block;top:50%;left:50%;transform:translate(-50%,-50%);z-index:20}.nx-modal__container{border-radius:4px;box-shadow:var(--shadow-large);background:var(--modal-background-color);color:var(--modal-text-color)}.nx-modal__content-wrapper{padding:40px 40px 48px;max-height:calc(100vh - 48px);overflow-y:auto}@media (max-width: 703px){.nx-modal__content-wrapper{max-height:70vh;padding:40px 32px 48px}}.nx-modal__close{position:absolute;top:16px;right:16px;height:24px}.nx-modal__close nx-icon{font-size:24px}:host-context([dir=rtl]) .nx-modal__close{right:auto;left:16px}@media (max-width: 703px){.nx-modal__position{width:100%}.nx-modal__container{position:relative;max-width:calc(100vw - 16px);max-height:70vh;margin:0 auto}}:host(.nx-modal--fixed-width) .nx-modal__container{width:736px;max-width:calc(100vw - 16px)}:host ::ng-deep .nx-modal__actions{height:72px;border-top:1px solid var(--modal-actions-border-color);display:flex;align-items:center;justify-content:flex-end;padding:0 40px;position:absolute;bottom:0;left:0;width:100%;border-bottom-left-radius:4px;border-bottom-right-radius:4px;background:var(--modal-actions-background-color)}:host ::ng-deep .nx-modal__content{overflow-y:auto;max-height:calc(100vh - 120px);padding:0 40px;margin:0 -40px}@media (max-width: 703px){:host ::ng-deep .nx-modal__content{max-height:calc(70vh - 120px);margin:0 -32px}}@media screen and (forced-colors: active){.nx-modal__content-wrapper,:host ::ng-deep .nx-modal__actions{border:2px solid CanvasText}}\n"]
    }]
  }], () => [{
    type: NxModalService
  }, {
    type: i3.EventManager
  }, {
    type: i0.ChangeDetectorRef
  }, {
    type: i1.FocusMonitor
  }], {
    _closeButton: [{
      type: ViewChild,
      args: ['closeButton']
    }],
    closeButtonLabel: [{
      type: Input
    }],
    body: [{
      type: Input,
      args: ['modalBody']
    }],
    hideOnEsc: [{
      type: Input
    }],
    hideOnClickOutside: [{
      type: Input
    }],
    showCloseIcon: [{
      type: Input
    }],
    size: [{
      type: Input,
      args: ['windowSize']
    }],
    closeEvent: [{
      type: Output,
      args: ['onModalClose']
    }]
  });
})();
class NxOpenModalOnClickDirective {
  /**
   * One or multiple template reference variables pointing to elements
   * which should trigger opening the modal on click.
   *
   * Value: A single template reference variable or an array of template reference variables.
   */
  set nxOpenModalOnClick(elements) {
    // also support the case where only one element is passed, check for the length property to be sure elements is an array
    if (Array.isArray(elements)) {
      this.elements = elements;
    } else {
      this.elements = [elements];
    }
    // add a click event listener to any element passed as property to this directive
    this.elements.forEach(el => {
      if (el.addEventListener) {
        el.addEventListener('click', this.clickHandler);
        // workaround until refactoring: if the reference is a component
        // reference the component has to implement a getter to its own elementRef
      } else if (el.elementRef) {
        el.elementRef.nativeElement.addEventListener('click', this.clickHandler);
      } else {
        console.warn(`nxOpenModalOnClick: Given Element doesn't appear to be an ElementRef.`, el);
      }
    });
  }
  constructor(templateRef, viewContainer, modalService) {
    this.templateRef = templateRef;
    this.viewContainer = viewContainer;
    this.modalService = modalService;
    this._destroyed = new Subject();
    /** @docs-private */
    this.clickHandler = event => {
      this.viewContainer.clear();
      // instantiate the template onto which this directive is applied
      const viewRef = this.viewContainer.createEmbeddedView(this.templateRef);
      // we need to mark the viewRef explicitly if the modal is used inside another
      // component that is using onPush change detection
      viewRef.markForCheck();
      event.preventDefault();
      event.stopPropagation();
    };
  }
  ngOnInit() {
    this.modalService.close$.pipe(takeUntil(this._destroyed)).subscribe(() => this.viewContainer.clear());
  }
  ngOnDestroy() {
    this._destroyed.next();
    this._destroyed.complete();
    this.elements.forEach(el => {
      if (el.removeEventListener) {
        el.removeEventListener('click', this.clickHandler);
      }
    });
  }
  static {
    this.ɵfac = function NxOpenModalOnClickDirective_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxOpenModalOnClickDirective)(i0.ɵɵdirectiveInject(i0.TemplateRef), i0.ɵɵdirectiveInject(i0.ViewContainerRef), i0.ɵɵdirectiveInject(NxModalService));
    };
  }
  static {
    this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
      type: NxOpenModalOnClickDirective,
      selectors: [["", "nxOpenModalOnClick", ""]],
      inputs: {
        nxOpenModalOnClick: "nxOpenModalOnClick"
      }
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxOpenModalOnClickDirective, [{
    type: Directive,
    args: [{
      selector: '[nxOpenModalOnClick]',
      standalone: true
    }]
  }], () => [{
    type: i0.TemplateRef
  }, {
    type: i0.ViewContainerRef
  }, {
    type: NxModalService
  }], {
    nxOpenModalOnClick: [{
      type: Input
    }]
  });
})();
class NxModalModule {
  // define the modal service on root level as an application wide singleton,
  // assuming that we only display one modal at a time, which seems quite reasonable
  // this is static and named "forRoot" by convention
  static forRoot() {
    return {
      ngModule: NxModalModule,
      providers: [NxModalService]
    };
  }
  static {
    this.ɵfac = function NxModalModule_Factory(__ngFactoryType__) {
      return new (__ngFactoryType__ || NxModalModule)();
    };
  }
  static {
    this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
      type: NxModalModule,
      imports: [A11yModule, OverlayModule, PortalModule, CommonModule, NxIconModule, NxModalComponent, NxOpenModalOnClickDirective, NxModalActionsDirective, NxModalContentDirective, /* new modal implementation */
      NxModalContainer, NxModalCloseDirective, NxModalTitleComponent],
      exports: [NxModalComponent, NxOpenModalOnClickDirective, NxModalActionsDirective, NxModalContentDirective, /* new modal implementation */
      NxModalContainer, NxModalCloseDirective, NxModalTitleComponent]
    });
  }
  static {
    this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
      providers: [NxDialogService, NX_MODAL_SCROLL_STRATEGY_PROVIDER],
      imports: [A11yModule, OverlayModule, PortalModule, CommonModule, NxIconModule, NxModalComponent, /* new modal implementation */
      NxModalContainer, NxModalTitleComponent]
    });
  }
}
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(NxModalModule, [{
    type: NgModule,
    args: [{
      imports: [A11yModule, OverlayModule, PortalModule, CommonModule, NxIconModule, NxModalComponent, NxOpenModalOnClickDirective, NxModalActionsDirective, NxModalContentDirective, /* new modal implementation */
      NxModalContainer, NxModalCloseDirective, NxModalTitleComponent],
      exports: [NxModalComponent, NxOpenModalOnClickDirective, NxModalActionsDirective, NxModalContentDirective, /* new modal implementation */
      NxModalContainer, NxModalCloseDirective, NxModalTitleComponent],
      // we need to keep these providers for backwards compatibility
      providers: [NxDialogService, NX_MODAL_SCROLL_STRATEGY_PROVIDER]
    }]
  }], null, null);
})();

/**
 * Generated bundle index. Do not edit.
 */

export { NX_MODAL_DATA, NX_MODAL_DEFAULT_OPTIONS, NX_MODAL_SCROLL_STRATEGY, NX_MODAL_SCROLL_STRATEGY_PROVIDER, NX_MODAL_SCROLL_STRATEGY_PROVIDER_FACTORY, NxDialogService, NxModalActionsDirective, NxModalAnimations, NxModalCloseDirective, NxModalComponent, NxModalConfig, NxModalContainer, NxModalContentDirective, NxModalModule, NxModalRef, NxModalService, NxModalTitleComponent, NxOpenModalOnClickDirective, throwNxDialogContentAlreadyAttachedError };
